import { Box, Button, Center, Flex, Grid, Input, Text } from "@chakra-ui/react";
import { useState } from "react";
import { InputField } from "components/input-field";
import { DropdownField } from "components/dropdown-field";
import OptimizedInput from "components/performance/attendance-input";
import { SendIcon } from "components/vectors/send-icon";
import { Toast } from "components/toast";
import { useAttendanceContext } from "contexts/attendance-context";
import {
  Attendance,
  AttendanceFactaFGTS,
  ConsultaPropostaFGTS,
  DigitationDataProps,
} from "components/atendimentos-components/atendimento-form/types";
import api from "api/api";
import { CopyIcon } from "@chakra-ui/icons";
import { InputSearch } from "components/input-search";
import { DocumentIcon } from "components/vectors/document-icon";
import { ButtonAcompanharPropostaColombo } from "components/atendimentos-components/atendimento-form/step-form/tabs-components/digitacao-fields/parts/acompanhar-proposta-colombo";
import {
  checkFieldsDigitacaoFacta,
  copyAttendanceDataToFactaForm,
  digitacaoPropostaErrors,
  fieldsDigitacaoFGTSFacta,
  mapeamentoConsultaPropostaFGTS,
} from "components/atendimentos-components/atendimento-form/step-form/tabs-components/digitacao-fields/parts/const";
import { openModalError } from "components/modal-error";
import { dropdownBancosSimulacaoFGTSColombo } from "../../simulacoes/utils";

export function DigitacaoAntecipacaoFGTSColombo() {
  const [consultandoProposta, setConsultandoProposta] = useState(false);
  const [isLoadingDigitation, setIsLoadingDigitation] = useState(false);
  const [errors, setErrors] = useState<
    Partial<Record<string | number | symbol, string>>
  >({});
  const { formControl, dispatch } = useAttendanceContext();

  const formValues = formControl.values as Attendance;
  const atendimentoFactaFGTS =
    formValues.atendimentoFactaFGTS ?? ({} as AttendanceFactaFGTS);
  let digitationData = formValues?.formularioFactaFGTS ?? {};

  const enableFactaFields =
    !atendimentoFactaFGTS.lockedByTrail &&
    atendimentoFactaFGTS.statusDigitacao === "FINISHED" &&
    atendimentoFactaFGTS.codeRequestDigitacao !== "SUCCESS";

  const isDisabledFactaFields =
    !!formValues.atendimentoFactaFGTS?.hasTokenDigitacao && !enableFactaFields;

  const onChangeDigitationData = (
    field: keyof DigitationDataProps,
    data: any
  ) => {
    const newValue = { ...digitationData, [field]: data };
    setErrors((errors) => {
      delete errors[field];
      return errors;
    });
    digitationData = newValue;
    dispatch({
      type: "changeField",
      payload: {
        fieldName: "formularioFactaFGTS",
        data: newValue,
      },
    });
  };

  const digitarProposta = async () => {
    if (!formValues.atendimentoFactaFGTS?.valorLiberadoAntecipacao) {
      return Toast({
        title: "Faça uma consulta FGTS para fazer a digitação",
      });
    }
    const { errors, isValid } = checkFieldsDigitacaoFacta(digitationData);
    if (!isValid) {
      Toast({ title: "Preencha todos os campos", status: "error" });
      return setErrors(errors);
    }
    if (formControl.pendingChanges)
      return Toast({
        title: "Salve as alterações do atendimento para fazer a digitação",
      });
    setIsLoadingDigitation(true);
    try {
      const { data } = await api.get(
        `/facta/fgts/enviar-proposta/${formValues.id}`
      );
      Toast({
        title: "Proposta em digitação",
        description: "Consulte em instantes.",
      });
      atendimentoFactaFGTS.ade = "";
      atendimentoFactaFGTS.formalizationLink = "";
      atendimentoFactaFGTS.lockedByTrail = null;
      atendimentoFactaFGTS.statusDigitacao = null;
      atendimentoFactaFGTS.codeRequestDigitacao = null;
      atendimentoFactaFGTS.hasTokenDigitacao = true;

      dispatch({
        type: "changeFieldWithInitial",
        payload: {
          data: atendimentoFactaFGTS,
          fieldName: "atendimentoFactaFGTS",
        },
      });
    } catch (e: any) {
      const error = e?.response?.data?.message;
      openModalError({
        message:
          digitacaoPropostaErrors.get(error) ?? "Erro ao digitar proposta",
      });
    } finally {
      setIsLoadingDigitation(false);
    }
  };

  const consultarProposta = async () => {
    setConsultandoProposta(true);
    try {
      const { data }: { data: ConsultaPropostaFGTS } = await api.get(
        `/facta/fgts/consultar-proposta/${formValues.id}`
      );

      atendimentoFactaFGTS.lockedByTrail = !!data.lockedByTrail;
      atendimentoFactaFGTS.statusDigitacao = data.statusDigitacao;
      atendimentoFactaFGTS.codeRequestDigitacao = data.codeRequestDigitacao;

      dispatch({
        type: "changeFieldWithInitial",
        payload: {
          data: atendimentoFactaFGTS,
          fieldName: "atendimentoFactaFGTS",
        },
      });

      mapeamentoConsultaPropostaFGTS({ data, dispatch, formValues });

      if (data.statusDigitacao === "PENDING") {
        Toast({
          title: "Digitação ainda está em andamento",
          status: "error",
        });
      } else if (data.statusDigitacao === "FINISHED") {
        if (data.codeRequestDigitacao === "SYSTEM_ERROR") {
          openModalError({
            message:
              data.messageResponseDigitacao ??
              "Ocorreu um erro na digitação da proposta",
          });
        }
      }
    } catch (e) {
      Toast({
        title: "Erro ao consultar proposta",
        description: "Tente novamente",
      });
    } finally {
      setConsultandoProposta(false);
    }
  };

  const parseBancoDigitacao = () =>
    dropdownBancosSimulacaoFGTSColombo.find(
      (crr) =>
        crr.value === formValues.atendimentoFactaFGTS?.bancoDigitacaoFGTSAPI
    )?.name;

  return (
    <Grid templateColumns="1fr" gap="16px">
      <Grid templateColumns="1fr 1fr 1fr" gap="16px">
        <Box>
          <Text mb="4px">Banco Digitação</Text>
          <Input value={parseBancoDigitacao()} w="100%" isReadOnly />
        </Box>
        <Box>
          <Text mb="4px">&nbsp;</Text>
          <Button
            w="100%"
            rightIcon={<SendIcon />}
            onClick={digitarProposta}
            isLoading={isLoadingDigitation}
            loadingText="Digitando Proposta"
            isDisabled={isDisabledFactaFields}
          >
            {enableFactaFields
              ? "Digitar Proposta Novamente"
              : "Digitar Proposta"}
          </Button>
        </Box>
        <Box>
          <Text mb="4px">&nbsp;</Text>
          <Button
            w="100%"
            rightIcon={<DocumentIcon width="22px" height="22px" />}
            onClick={consultarProposta}
            isLoading={consultandoProposta}
            loadingText="Consultando Proposta"
            isDisabled={
              !!formValues?.atendimentoFactaFGTS?.ade ||
              !formValues.atendimentoFactaFGTS?.hasTokenDigitacao
            }
          >
            Consultar Proposta
          </Button>
        </Box>
      </Grid>
      <Grid templateColumns="repeat(3, 1fr)" gap="16px">
        <Box gridArea="auto / span 1">
          <Text mb="4px">ADE</Text>
          <OptimizedInput
            value={formValues?.atendimentoFactaFGTS?.ade || ""}
            isReadOnly
          />
        </Box>
        <Box gridArea="auto / span 1">
          <Text mb="4px">Link Formalização</Text>
          <Flex alignItems="center" gap="10px">
            <OptimizedInput
              value={formValues?.atendimentoFactaFGTS?.formalizationLink || ""}
              isReadOnly
            />
          </Flex>
        </Box>
        <Box gridArea="auto / span 1">
          <Text mb="4px">Observação</Text>
          <Flex alignItems="center" gap="10px">
            <ButtonAcompanharPropostaColombo attendance={formValues} />
          </Flex>
        </Box>
      </Grid>
      <Grid
        templateColumns="repeat(12, 1fr)"
        gap="16px"
        padding="20px 10%"
        borderRadius="6px"
        border="1px solid var(--chakra-colors-custom-gray)"
        bg="gray.100"
        pos="relative"
      >
        <Center flexDir="column" gridArea="auto / span 12" mb="24px">
          <Text fontSize="20px" fontWeight="medium">
            Formulário {formValues.bancoDigitacao ?? "Banco"}
          </Text>
        </Center>
        {isDisabledFactaFields ? null : (
          <Button
            pos="absolute"
            top="16px"
            right={"10px"}
            leftIcon={<CopyIcon />}
            size="sm"
            onClick={() => copyAttendanceDataToFactaForm(formValues, dispatch)}
          >
            Copiar dados do atendimento
          </Button>
        )}
        {fieldsDigitacaoFGTSFacta.map(
          ({
            fieldType,
            key,
            label,
            componentProps,
            format,
            containerProps,
            options,
            onChange,
            ufKey,
            isVisible,
          }) => {
            const currentValue = digitationData?.[key];
            const isVisibleField = isVisible?.(formValues) ?? true;
            if (isVisibleField)
              if (fieldType === "input") {
                const { inputProps, ...rest } = componentProps || {};
                return (
                  <Grid
                    templateColumns="220px auto"
                    alignItems="center"
                    key={key}
                    gridArea="auto / span 12"
                    gap="20px"
                    {...containerProps}
                  >
                    <InputField
                      title={label}
                      titleProps={{
                        mb: "0px",
                        whiteSpace: "nowrap",
                        textAlign: "end",
                      }}
                      inputProps={{ w: "100%", bg: "#fff", ...inputProps }}
                      popoverProps={{ containerStyles: { width: "100%" } }}
                      errorMessage={errors[key]}
                      isDisabled={isDisabledFactaFields}
                      // closeErrorOnBlur={false}
                      onChange={(e) => {
                        if (onChange) {
                          onChange(
                            e.target.value,
                            onChangeDigitationData,
                            digitationData
                          );
                        } else
                          onChangeDigitationData(
                            key,
                            format?.(e.target.value, digitationData).raw ??
                              e.target.value
                          );
                      }}
                      value={
                        format?.(digitationData?.[key], digitationData).value ??
                        digitationData?.[key] ??
                        ""
                      }
                      {...rest}
                    />
                  </Grid>
                );
              } else if (fieldType === "dropdown") {
                const { dropdownProps, ...rest } = componentProps || {};
                return (
                  <Grid
                    templateColumns="220px auto"
                    alignItems="center"
                    key={key}
                    gridArea="auto / span 12"
                    gap="20px"
                    {...containerProps}
                  >
                    <DropdownField
                      title={label}
                      titleProps={{
                        mb: "0px",
                        whiteSpace: "nowrap",
                        textAlign: "end",
                      }}
                      errorMessage={errors[key]}
                      dropdownProps={{
                        w: "100%",
                        containerStyles: { w: "100%" },
                        bg: "#fff",
                        isDisabled: isDisabledFactaFields,
                        ...dropdownProps,
                      }}
                      popoverProps={{ containerStyles: { width: "100%" } }}
                      onChange={(value) => {
                        if (onChange) {
                          onChange(
                            value,
                            onChangeDigitationData,
                            digitationData
                          );
                        } else {
                          onChangeDigitationData(key, value);
                        }
                      }}
                      value={
                        format?.(digitationData?.[key], digitationData).value ??
                        digitationData?.[key] ??
                        ""
                      }
                      {...rest}
                    />
                  </Grid>
                );
              } else if (fieldType === "inputSearch") {
                const { inputProps, ...rest } = componentProps || {};
                const id = `InputSearch-${key}`;

                return (
                  <Grid
                    templateColumns="220px auto"
                    alignItems="center"
                    key={key}
                    gridArea="auto / span 12"
                    gap="20px"
                    {...containerProps}
                  >
                    <Text mb="0px" whiteSpace="nowrap" textAlign="end">
                      {label}
                    </Text>
                    <InputSearch
                      onChange={(optionValue: {
                        cityName: string;
                        uf: string;
                      }) => {
                        onChangeDigitationData(key, optionValue.cityName);
                        if (ufKey)
                          onChangeDigitationData(ufKey, optionValue.uf);
                      }}
                      clearInput={false}
                      searchKeys={["name"]}
                      errorMessage={errors[key]}
                      checkIsInclude={(option) =>
                        digitationData?.[key] === option.value.cityName
                      }
                      optionLabelRender={(option) =>
                        `${option.name} - ${option.value.uf}`
                      }
                      value={
                        currentValue && ufKey
                          ? `${currentValue} - ${digitationData[ufKey]}`
                          : undefined
                      }
                      options={options}
                      inputProps={{
                        id,
                        bg: "#fff",
                        onBlur: (e) => {
                          if (
                            ufKey &&
                            digitationData[key] &&
                            digitationData[ufKey]
                          )
                            e.target.value = `${digitationData[key]} - ${digitationData[ufKey]}`;
                          else e.target.value = "";
                        },
                        isDisabled: isDisabledFactaFields,
                        ...inputProps,
                      }}
                      {...rest}
                    />
                  </Grid>
                );
              }
          }
        )}
      </Grid>
    </Grid>
  );
}
